import { useCallback, useEffect, useRef, useState } from 'react';
import { CODE_PROGRAM_MAP, resolveProgramCode, resolveProgramName } from '../../utils/dataNormalizer';
import { sanitizeRecords, validateVmtRecord } from '../../utils/vmt';
import { formatVmtSendErrorMessage } from '../../utils/vmtSendError';
import { recordSuccessfulCall, getAverageDuration, getMaxDuration } from '../../utils/bacTimingStorage';

const throttleDelayMs = Number(import.meta.env?.VITE_VMT_SEND_DELAY_MS || 150);

const useSendTabQueue = ({
  formValues,
  dealerContext,
  userEmail,
  sendMutation,
  queryClient,
  emitToast,
  isMountedRef,
  setErrors,
  clearDraft,
}) => {
  const [queue, setQueue] = useState([]);
  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sendProgress, setSendProgress] = useState({
    isActive: false,
    progress: 0,
    startTime: null,
    estimatedDuration: null,
  });

  useEffect(() => {
    if (!sendProgress.isActive || !sendProgress.startTime || !sendProgress.estimatedDuration) {
      return;
    }

    const interval = setInterval(() => {
      setSendProgress((prev) => {
        if (!prev.isActive || !prev.startTime || !prev.estimatedDuration) {
          return prev;
        }
        const elapsed = Date.now() - prev.startTime;
        let currentEstimate = prev.estimatedDuration;
        if (elapsed > currentEstimate) {
          currentEstimate = elapsed + 1000;
        }
        const progressPercent = Math.min((elapsed / currentEstimate) * 100, 90);
        return {
          ...prev,
          progress: progressPercent,
          estimatedDuration: currentEstimate,
        };
      });
    }, 300);

    return () => clearInterval(interval);
  }, [sendProgress.isActive, sendProgress.startTime, sendProgress.estimatedDuration]);

  const runQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    while (queueRef.current.length > 0) {
      const nextPayload = queueRef.current[0];
      if (isMountedRef.current) {
        setIsProcessing(true);
      }
      console.info('[SendSelection] processing next payload', {
        context: nextPayload.context,
        remaining: queueRef.current.length,
      });

      const sendStartTime = Date.now();
      const averageDuration = getAverageDuration();
      setSendProgress({
        isActive: true,
        progress: 0,
        startTime: sendStartTime,
        estimatedDuration: averageDuration,
      });

      try {
        const data = await sendMutation.mutateAsync({
          records: nextPayload.records,
          userEmail: nextPayload.userEmail,
        });
        console.info('[SendSelection] send succeeded', {
          snapshotStatus: data?.snapshot?.snapshotStatus || 'unknown',
        });

        const actualDuration = Date.now() - sendStartTime;
        const maxDuration = getMaxDuration();
        const updatedEstimate = Math.max(averageDuration, maxDuration, actualDuration);

        setSendProgress((prev) => ({
          ...prev,
          progress: 100,
          estimatedDuration: updatedEstimate,
        }));

        recordSuccessfulCall(actualDuration);

        setTimeout(() => {
          setSendProgress({
            isActive: false,
            progress: 0,
            startTime: null,
            estimatedDuration: null,
          });
        }, 500);

        const snapshotStatus = data?.snapshot?.snapshotStatus;
        let successMessage = 'Submission succeeded.';
        let severity = 'success';
        if (snapshotStatus === 'failed') {
          successMessage = 'Submission succeeded, but snapshot capture failed.';
          severity = 'warning';
        } else if (snapshotStatus === 'partial') {
          successMessage = 'Submission succeeded. Snapshot capture partially failed.';
          severity = 'warning';
        }
        emitToast(successMessage, severity, {
          navigateToHistory: true,
          refreshDealer: true,
          refreshProgramName: nextPayload?.context?.programName || null,
          refreshBac: nextPayload?.context?.bac || null,
          refreshDelayMs: 1500,
        });
      } catch (error) {
        setSendProgress({
          isActive: false,
          progress: 0,
          startTime: null,
          estimatedDuration: null,
        });

        const message = formatVmtSendErrorMessage(error);
        console.error('[SendSelection] send failed', { message, error });
        emitToast(message, 'error', { navigateToHistory: true });
      } finally {
        queueRef.current.shift();
        if (isMountedRef.current) {
          setQueue([...queueRef.current]);
        }
        try {
          await queryClient.invalidateQueries({ queryKey: ['vmtSendHistory'] });
        } catch (invalidateError) {
          console.error('Failed to invalidate send history queries', invalidateError);
        }
        if (queueRef.current.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, throttleDelayMs));
        }
      }
    }

    processingRef.current = false;
    if (isMountedRef.current) {
      setIsProcessing(false);
    }
  }, [emitToast, isMountedRef, queryClient, sendMutation]);

  const enqueueSend = useCallback(() => {
    const programNameFallback =
      formValues.programName ||
      formValues.programLabel ||
      dealerContext?.programName ||
      dealerContext?.programLabel ||
      '';
    const candidateProgramCode =
      formValues.programCode || dealerContext?.programCode || dealerContext?.program || null;
    const resolvedProgramName =
      resolveProgramName(candidateProgramCode) ||
      resolveProgramName(programNameFallback) ||
      (candidateProgramCode && CODE_PROGRAM_MAP[candidateProgramCode]) ||
      programNameFallback;
    const resolvedProgramCode =
      resolveProgramCode(candidateProgramCode) ||
      resolveProgramCode(programNameFallback) ||
      candidateProgramCode ||
      null;

    const mergedFormValues = {
      ...formValues,
      programName: resolvedProgramName,
      programLabel: formValues.programLabel || resolvedProgramName,
      programCode: resolvedProgramCode,
    };

    const sanitized = sanitizeRecords([mergedFormValues])[0];
    const validation = validateVmtRecord(sanitized);
    if (Object.keys(validation).length > 0) {
      console.error('[SendSelection] validation failed', { validation, payload: sanitized });
      setErrors(validation);
      return;
    }
    console.info('[SendSelection] enqueue', {
      bac: sanitized.bac,
      programName: sanitized.programName || dealerContext?.programName,
    });
    setErrors({});
    const context = {
      bac: dealerContext?.bac || sanitized.bac,
      programName:
        sanitized.programName ||
        resolveProgramName(sanitized.programCode) ||
        dealerContext?.programName ||
        dealerContext?.programLabel ||
        sanitized.program ||
        '',
    };
    const payload = {
      records: [sanitized],
      userEmail: userEmail || null,
      context,
    };
    queueRef.current = [...queueRef.current, payload];
    setQueue([...queueRef.current]);
    clearDraft?.();
    runQueue();
    emitToast('Selection queued for send', 'info');
  }, [
    clearDraft,
    dealerContext,
    emitToast,
    formValues,
    runQueue,
    setErrors,
    userEmail,
  ]);

  return {
    queue,
    isProcessing,
    sendProgress,
    enqueueSend,
  };
};

export default useSendTabQueue;
